import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ClipboardList, Coffee, HandCoins } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import HomeCalculator from './HomeCalculator';
import AmazonLinksSection from './sections/AmazonLinksSection';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabase';
import '../styles/HomePage.css';

// Hidden for now per David — easy to flip back on.
const SHOW_HERO_BYLINE = false;

const HomePage = () => {
  const location = useLocation();
  const showDeletionPending = location.state?.deletionPending === true;
  const { user } = useAuth();
  const [recentPlan, setRecentPlan] = useState(null);
  usePageTitle(null);

  useEffect(() => {
    if (!user) {
      setRecentPlan(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('projects')
      .select('id, name, updated_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(1)
      .single()
      .then(({ data }) => {
        if (!cancelled) setRecentPlan(data ?? null);
      });
    return () => { cancelled = true; };
  }, [user]);

  return (
    <div className="hp-page">

      {/* ── Deletion Pending Banner ── */}
      {showDeletionPending && (
        <div className="hp-deletion-pending-banner">
          <strong>Your deletion request has been received.</strong>
          <p>
            We'll permanently delete your account within 7 days.
            You'll get a confirmation email once your data has been fully removed from our servers.
          </p>
        </div>
      )}

      {/* ── Hero ── */}
      <div className="hp-hero">
        <div className="hp-hero-lockup">
          <div className="hp-hero-emoji">
            <img src={`${process.env.PUBLIC_URL}/images/Icing_Calc_logo_May2026.png`} alt="The Icing Calculator" className="hp-hero-logo" />
          </div>
          <div className="hp-hero-text">
            <h1 className="hp-hero-title">The Icing Calculator</h1>
            <p className="hp-hero-tagline">Less guessing. More icing.</p>
            <p className="hp-hero-sub">
              Calculate how much royal icing you need{' '}
              <br />
              by color - by cookie - by order
            </p>
            {SHOW_HERO_BYLINE && (
              <p className="hp-hero-byline">
                Built by Michele, a working cookie decorator, who was tired of guessing at icing math.{' '}
                <Link to="/about-us">read our story →</Link>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Resume Plan Card (logged-in users with saved plans only) ── */}
      {recentPlan && (
        <Link
          to={`/pro?resumePlan=${recentPlan.id}`}
          className="hp-resume-card"
        >
          <div className="hp-resume-icon"><ClipboardList size={24} aria-hidden="true" /></div>
          <div className="hp-resume-text">
            <span className="hp-resume-label">Resume where you left off</span>
            <span className="hp-resume-name">{recentPlan.name}</span>
          </div>
          <div className="hp-resume-arrow">→</div>
        </Link>
      )}

      {/* ── Inline Basic Calculator + Tip Jar (side-by-side on desktop) ── */}
      <div className="hp-calc-row">
        <div className="hp-calc-col">
          <HomeCalculator />
        </div>
        <div className="hp-tip-jar-col">
          <div className="hp-tip-jar-card">
            <div className="hp-tip-jar-icon"><Coffee size={26} aria-hidden="true" /></div>
            <h3 className="hp-tip-jar-title">Love the calculator?</h3>
            <p className="hp-tip-jar-text">
              Buying me a virtual coffee (or a bag of powdered sugar!) is a great way to support future updates and help keep this tool available for the cookie community.
            </p>
            <a
              href="https://buy.stripe.com/4gMdR85j5gftgsaaiB0RG01"
              target="_blank"
              rel="noopener noreferrer"
              className="hp-tip-jar-btn"
            >
              <span className="hp-tip-jar-btn-label">
                <HandCoins size={18} aria-hidden="true" />
                Leave a Tip
              </span>
            </a>
            <p className="hp-tip-jar-sub">Processed securely through Stripe</p>
          </div>
        </div>
      </div>



      {/* ── Recommended Products (Amazon links) ── */}
      <AmazonLinksSection title="Recommended Tools & Supplies" />

    </div>
  );
};

export default HomePage;
